/**
 * Lesson *content schema* — the typed shape of Sprocket's authored curriculum,
 * plus the one sample chapter set that exercises it end-to-end.
 *
 * This is **authored content, not a live AI**. A future task (#704) may layer a
 * Claude-as-tutor experience on top, but the engine, menu, progress and
 * navigation are all driven by the plain data described here.
 *
 * The model mirrors the original menu-driven lesson list (the one with the `*`
 * completion markers):
 *
 *   chapter ──┬── title         a short, kid-friendly lesson name
 *             ├── story         robot story framing that sets the scene
 *             ├── goal          the goal / payoff once the chapter is done
 *             └── steps[] ──┬── explanation    what to learn (scrolling prose)
 *                           ├── tryIt          a hands-on "try it" prompt
 *                           └── successCheck   how we know the kid succeeded
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  The immediate-window seam (#701)
 * ───────────────────────────────────────────────────────────────────────────
 * A step's `successCheck` is **pure, serializable data** — never a function —
 * so authored content stays declarative and the engine never reaches into the
 * REPL. The immediate window (#701, not built yet) is expected to:
 *
 *   1. let the kid run their try-it code,
 *   2. build an {@link ExerciseContext} from what happened (captured output,
 *      and whether the kid tapped "I did it" for manual steps),
 *   3. hand it to the engine (`engine.submitExercise(ctx)`), which calls
 *      {@link evaluateSuccessCheck} to decide whether to advance.
 *
 * That keeps this engine fully testable today and gives #701 a tiny, stable
 * contract to implement against — no premature coupling to a REPL that does
 * not exist.
 */

/** How a step decides the kid has succeeded. Pure data — see the seam note. */
export type SuccessCheck =
  /** The kid self-confirms (taps "I did it!"). Good for open-ended play. */
  | { kind: "manual" }
  /** Pass when the captured output contains `text` (case-insensitive). */
  | { kind: "output-contains"; text: string }
  /** Pass when the trimmed captured output equals `text` exactly. */
  | { kind: "output-equals"; text: string }
  /** Pass when the captured output matches this regex (source + optional flags). */
  | { kind: "output-matches"; pattern: string; flags?: string };

/**
 * What the kid did in the immediate window, handed to the engine by #701.
 * Deliberately minimal: just enough for the success checks above to evaluate.
 */
export interface ExerciseContext {
  /** Captured stdout/stderr text from the kid's run (empty string if none). */
  output: string;
  /** True when the kid explicitly confirmed a `manual` step. */
  confirmed?: boolean;
}

/** A single hands-on step within a chapter. */
export interface Step {
  /** Stable id, unique within its chapter (used as a progress key). */
  id: string;
  /** Scrolling explanation prose — what this step teaches. */
  explanation: string;
  /** A concrete "try it" prompt for the immediate window. */
  tryIt: string;
  /** How success is detected; drives auto-advance. */
  successCheck: SuccessCheck;
}

/** A chapter / lesson: story framing, a goal, and an ordered list of steps. */
export interface Chapter {
  /** Stable id, globally unique (used as a progress key). */
  id: string;
  /** Short, kid-friendly lesson title (shown in the menu). */
  title: string;
  /** Robot story framing that sets the scene for the lesson. */
  story: string;
  /** The goal / payoff the kid earns by finishing the chapter. */
  goal: string;
  /** Ordered steps the kid works through. */
  steps: Step[];
}

/**
 * Evaluate a step's success check against what happened in the immediate
 * window. Total and side-effect free: a malformed regex simply fails the check
 * rather than throwing, so bad authored content can never crash the lesson.
 */
export function evaluateSuccessCheck(
  check: SuccessCheck,
  ctx: ExerciseContext,
): boolean {
  switch (check.kind) {
    case "manual":
      return ctx.confirmed === true;
    case "output-contains":
      return ctx.output.toLowerCase().includes(check.text.toLowerCase());
    case "output-equals":
      return ctx.output.trim() === check.text.trim();
    case "output-matches":
      try {
        return new RegExp(check.pattern, check.flags).test(ctx.output);
      } catch {
        // Malformed authored regex — never throw at a kid mid-lesson.
        return false;
      }
  }
}

/* -------------------------------------------------------------------------- */
/*  Sample curriculum — robots / gadgets theme 🤖                             */
/* -------------------------------------------------------------------------- */

/**
 * One fully-authored sample chapter (plus a second so the menu and
 * lesson-to-lesson navigation are exercised). These prove the schema end to
 * end; richer content lands in follow-up authoring tasks.
 */
export const SAMPLE_CHAPTERS: Chapter[] = [
  {
    id: "wake-up-beep",
    title: "Wake Up Beep",
    story:
      "Beep the robot is powered down in the workshop. The only way to switch " +
      "Beep on is to talk to it in Python. Let's send Beep its very first words!",
    goal: "By the end, Beep will say hello — and you'll have run real Python.",
    steps: [
      {
        id: "first-print",
        explanation:
          "Robots talk using the print() command. Whatever you put inside the " +
          "quotes is what the robot says out loud.",
        tryIt: 'Type print("beep beep") and run it to wake Beep up.',
        successCheck: { kind: "output-contains", text: "beep" },
      },
      {
        id: "say-hello",
        explanation:
          "Beep is awake! Now let's make it polite. You can print any message " +
          "you like — try greeting Beep by name.",
        tryIt: 'Make Beep say hello, like print("Hello, Beep!").',
        successCheck: { kind: "output-contains", text: "hello" },
      },
      {
        id: "robot-math",
        explanation:
          "Robots love numbers. print() can show the answer to a sum, too — " +
          "Python does the math for you.",
        tryIt: "Print the answer to 2 + 2 by running print(2 + 2).",
        successCheck: { kind: "output-matches", pattern: "\\b4\\b" },
      },
    ],
  },
  {
    id: "teach-beep-to-remember",
    title: "Teach Beep to Remember",
    story:
      "Beep keeps forgetting things the moment you say them. Robots remember " +
      "by storing values in *variables* — labelled boxes that hold a value.",
    goal: "By the end, Beep will remember its own name using a variable.",
    steps: [
      {
        id: "first-variable",
        explanation:
          "A variable is a name that points at a value. We make one with an " +
          'equals sign: name = "Beep". Now the word name remembers "Beep".',
        tryIt: 'Make a variable: name = "Beep" — then print(name).',
        successCheck: { kind: "output-contains", text: "beep" },
      },
      {
        id: "change-the-memory",
        explanation:
          "Variables can change. Give name a new value and Beep remembers the " +
          "new one instead. Robots learn by updating what they remember!",
        tryIt: 'Set name = "Sprocket" and print(name) to see it change.',
        successCheck: { kind: "manual" },
      },
    ],
  },
];
